/**
 * Google reCAPTCHA v3 (invisible / score-based) frontend helper.
 *
 * CRA exposes env vars only with the REACT_APP_ prefix, so the site key is
 * read from REACT_APP_RECAPTCHA_SITE_KEY. When the key is absent (dev), every
 * call is a graceful no-op returning null — the backend treats a missing token
 * as "skip verification" in dev too, so nothing breaks.
 */
const SITE_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim();

export const RECAPTCHA_ENABLED = Boolean(SITE_KEY);

let loadPromise = null;

function loadScript() {
  if (!SITE_KEY) return Promise.resolve(false);
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve) => {
    if (typeof window === 'undefined') { resolve(false); return; }
    if (window.grecaptcha && window.grecaptcha.execute) { resolve(true); return; }
    const existing = document.querySelector('script[data-recaptcha="v3"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = 'v3';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return loadPromise;
}

/** Preload the reCAPTCHA script (and its badge). Safe to call on app init. */
export function initRecaptcha() {
  if (SITE_KEY) loadScript();
}

/**
 * Execute reCAPTCHA for a given action and return a one-time token.
 * Returns null if reCAPTCHA is not configured or fails to load — callers
 * should send the (possibly null) token; the backend handles the no-op path.
 */
export async function getRecaptchaToken(action = 'submit') {
  if (!SITE_KEY) return null;
  try {
    const ok = await loadScript();
    if (!ok || !window.grecaptcha || !window.grecaptcha.execute) return null;
    await new Promise((res) => window.grecaptcha.ready(res));
    return await window.grecaptcha.execute(SITE_KEY, { action });
  } catch {
    return null;
  }
}
